// @flow

import * as React from 'react';
import Box from 'sail/Box';
import {ContentHeader} from 'sail/Content';
import {ContentTab} from 'sail/ContentTabs';
import Toggle, {ToggleItem} from 'sail/Toggle';
import {usePrefs, useLocation} from 'docs/data';
import {useActivePage} from 'docs/hooks';
import {useContentAnimation} from 'docs/shell/content/ContentAnimationProvider';
import './AcceptPaymentCustomPageSelector.scss';
import {FormattedMessage} from 'react-intl';
import messages from './AcceptPaymentCustomPageSelector.messages';

type Platform = {
  id: 'web' | 'ios' | 'android' | 'react-native' | 'plugins',
  title: {id: string, description: string, defaultMessage: string},
};

const PLATFORMS: Array<Platform> = [
  {id: 'web', title: messages.web},
  {id: 'ios', title: messages.ios},
  {id: 'android', title: messages.android},
  {id: 'react-native', title: messages.reactNative},
  {id: 'plugins', title: messages.plugins},
];

const UIS = [
  {id: 'checkout', title: messages.checkout},
  {id: 'elements', title: messages.elements},
];

const PrebuiltOrCustomSelector = () => {
  const {setPref} = usePrefs();
  const activePage = useActivePage();
  const {location, replaceLocation} = useLocation();
  const {setAnimationDirection} = useContentAnimation();
  const activeIndex = UIS.findIndex((ui) => ui.id === activePage.prefs.ui);

  const tabs = UIS.map((ui, index) => (
    <ContentTab
      className="PrebuiltOrCustomSelector-tab"
      key={ui.id}
      size="medium"
      active={ui.id === activePage.prefs.ui}
      onClick={() => {
        setAnimationDirection(activeIndex, index);
        replaceLocation({
          query: {...location.query, ui: ui.id},
        });
        setPref('ui', ui.id);
      }}
    >
      <FormattedMessage {...ui.title} />
    </ContentTab>
  ));

  return (
    <ContentHeader size="medium" padding={{top: 16}} start={{content: tabs}} />
  );
};

type AcceptPaymentCustomPageSelectorProps = {|
  showReact: boolean,
  showPlugins: boolean,
|};

export const getPlatformsToRender = (
  props: AcceptPaymentCustomPageSelectorProps,
): Array<Platform> =>
  PLATFORMS.filter((platform) => {
    if (platform.id === 'plugins') {
      return props.showPlugins;
    }
    if (platform.id === 'react-native') {
      return props.showReact;
    }
    return true;
  });

export const AcceptPaymentCustomPageSelector = (
  props: AcceptPaymentCustomPageSelectorProps,
) => {
  const {setPref} = usePrefs();
  const activePage = useActivePage();
  const showPrebuiltOrCustomSelector = activePage.prefs.platform === 'web';
  const {setAnimationDirection} = useContentAnimation();
  const {location, replaceLocation} = useLocation();
  const activeIndex = PLATFORMS.findIndex(
    (platform) => platform.id === activePage.prefs.platform,
  );

  return (
    <Box className="AcceptPaymentCustomPageSelector">
      <Toggle type="radio">
        {getPlatformsToRender(props).map((platform, index) => (
          <ToggleItem
            key={platform.id}
            label={<FormattedMessage {...platform.title} />}
            checked={platform.id === activePage.prefs.platform}
            onChange={() => {
              setAnimationDirection(activeIndex, index);
              const query = {...location.query, platform: platform.id};
              delete query.ui;
              replaceLocation({
                query,
              });
              setPref('platform', platform.id);
            }}
          />
        ))}
      </Toggle>
      {showPrebuiltOrCustomSelector && <PrebuiltOrCustomSelector />}
    </Box>
  );
};
